import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/services/apiClient';
import './ftp-editor.css';

export interface FtpVariableInserterProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (variableName: string) => void;
  variables: Record<string, string>;
  variableDefaults?: Record<string, string>;
}

export function FtpVariableInserter({
  isOpen,
  onClose,
  onSelect,
  variables,
  variableDefaults: defaultsProp,
}: FtpVariableInserterProps) {
  const [search, setSearch] = useState('');
  const [varDefaults, setVarDefaults] = useState<Record<string, string>>(defaultsProp || {});

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (defaultsProp) { setVarDefaults(defaultsProp); return; }
    apiClient.get('/marketing/settings').then((res) => {
      const vd = res.data?.data?.variableDefaults;
      if (vd && typeof vd === 'object') setVarDefaults(vd);
    }).catch(() => {});
  }, [defaultsProp]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const entries = Object.entries(variables);
    if (!q) return entries;
    return entries.filter(
      ([token, desc]) =>
        token.toLowerCase().includes(q) || desc.toLowerCase().includes(q),
    );
  }, [variables, search]);

  if (!isOpen) return null;

  return createPortal(
    <div className="ftp-modal-overlay active">
      <div
        className="ftp-modal ftp-variable-modal"
        role="dialog"
        aria-labelledby="ftp-variable-modal-title"
      >
        <div className="ftp-modal-header">
          <h3 id="ftp-variable-modal-title">Insert Variable</h3>
          <button type="button" className="ftp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="ftp-modal-body">
          <p className="text-[11px] text-slate-400 mb-2">
            Missing values use your account defaults. Override per-variable inline: <code>{'{{first_name [[Friend]]}}'}</code>
          </p>
          <input
            type="search"
            className="ftp-variable-search"
            placeholder="Search variables…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
          <div className="ftp-variable-list">
            {filtered.map(([token, desc]) => {
              const varName = token.replace(/^\{\{|\}\}$/g, '');
              const defaultVal = varDefaults[varName];
              return (
                <div
                  key={token}
                  className="ftp-variable-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    onSelect(token);
                    onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(token);
                      onClose();
                    }
                  }}
                >
                  <div className="ftp-variable-item-row">
                    <span className="ftp-variable-name">{token}</span>
                    <span className="ftp-variable-desc">{desc}</span>
                  </div>
                  {defaultVal && (
                    <span className="ftp-variable-default">Default: {defaultVal}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
